import { v4 as uuidv4 } from 'uuid'
import { USER_JOURNEY_NAMES } from './constants'

export const MOBILE_USER_JOURNEY_CHANNEL = 'afterpay-mobile-uj'
export const MOBILE_USER_JOURNEY_PROTOCOL_VERSION = 1
export const MOBILE_OWNERSHIP_READY_TIMEOUT_MS = 750

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function isUuid(value: unknown): value is string {
  return typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
}

interface Envelope {
  channel: typeof MOBILE_USER_JOURNEY_CHANNEL
  version: typeof MOBILE_USER_JOURNEY_PROTOCOL_VERSION
  messageId: string
}

export interface OwnershipOffer extends Envelope {
  type: 'ownership_offer'
  checkoutFlowToken: string
  sourceSequence: 0
  payload: {
    owner: 'afterpay-mobile'
    journeyName: typeof USER_JOURNEY_NAMES.CHECKOUT
    startedAtEpochMs: number
  }
}

export interface OwnershipCommit extends Envelope {
  type: 'ownership_commit'
  checkoutFlowToken: string
  sourceSequence: 1
  payload: {
    owner: 'afterpay-mobile'
    journeyId?: string
  }
}

export interface OwnershipRelease extends Envelope {
  type: 'ownership_release'
  checkoutFlowToken: string
  sourceSequence: 1
  payload: {
    owner: 'rocketship'
    reason: 'mobile_runtime_unavailable'
  }
}

export type OwnershipCommand = OwnershipOffer | OwnershipCommit | OwnershipRelease

type OwnershipAck = Envelope & {
  type: 'ownership_ack'
  checkoutFlowToken: string
  sourceSequence: 0
  payload: { owner: 'afterpay-mobile' }
}

type OwnershipCommitted = Envelope & {
  type: 'ownership_committed'
  checkoutFlowToken: string
  sourceSequence: 1
  payload: { owner: 'afterpay-mobile'; journeyId?: string }
}

type OwnershipNack = Envelope & {
  type: 'ownership_nack'
  checkoutFlowToken: string
  sourceSequence: 0
  payload: {
    owner: 'rocketship'
    reason: 'unsupported_version' | 'ownership_unavailable'
  }
}

export const createOwnershipReady = (): Envelope & { type: 'ownership_ready'; payload: { owner: 'rocketship' } } => ({
  channel: MOBILE_USER_JOURNEY_CHANNEL,
  version: MOBILE_USER_JOURNEY_PROTOCOL_VERSION,
  type: 'ownership_ready',
  messageId: uuidv4(),
  payload: { owner: 'rocketship' },
})

export function createOwnershipResponse(type: 'ownership_ack', checkoutFlowToken: string): OwnershipAck
export function createOwnershipResponse(type: 'ownership_committed', checkoutFlowToken: string, journeyId?: string): OwnershipCommitted
export function createOwnershipResponse(type: 'ownership_nack', checkoutFlowToken: string, reason: OwnershipNack['payload']['reason']): OwnershipNack
export function createOwnershipResponse(
  type: 'ownership_ack' | 'ownership_committed' | 'ownership_nack',
  checkoutFlowToken: string,
  detail?: string
): OwnershipAck | OwnershipCommitted | OwnershipNack {
  const envelope = {
    channel: MOBILE_USER_JOURNEY_CHANNEL,
    version: MOBILE_USER_JOURNEY_PROTOCOL_VERSION,
    messageId: uuidv4(),
    checkoutFlowToken,
  } as const

  if (type === 'ownership_ack') {
    return { ...envelope, type, sourceSequence: 0, payload: { owner: 'afterpay-mobile' } }
  }
  if (type === 'ownership_nack') {
    if (detail !== 'unsupported_version' && detail !== 'ownership_unavailable') throw new Error('Ownership NACK requires a reason')
    return { ...envelope, type, sourceSequence: 0, payload: { owner: 'rocketship', reason: detail } }
  }
  return { ...envelope, type, sourceSequence: 1, payload: { owner: 'afterpay-mobile', ...(detail === undefined ? {} : { journeyId: detail }) } }
}

export const parseOwnershipCommand = (data: unknown): OwnershipCommand | undefined => {
  if (typeof data !== 'string' || data.length > 16 * 1024) return undefined

  let value: unknown
  try {
    value = JSON.parse(data)
  } catch {
    return undefined
  }
  if (!isRecord(value) || value.channel !== MOBILE_USER_JOURNEY_CHANNEL || value.version !== MOBILE_USER_JOURNEY_PROTOCOL_VERSION) return undefined
  if (!isUuid(value.messageId) || !isUuid(value.checkoutFlowToken) || !isRecord(value.payload)) return undefined

  if (
    value.type === 'ownership_offer' &&
    value.sourceSequence === 0 &&
    value.payload.owner === 'afterpay-mobile' &&
    value.payload.journeyName === USER_JOURNEY_NAMES.CHECKOUT &&
    typeof value.payload.startedAtEpochMs === 'number' &&
    Number.isFinite(value.payload.startedAtEpochMs) &&
    value.payload.startedAtEpochMs >= 0
  ) {
    return value as unknown as OwnershipOffer
  }
  if (
    value.type === 'ownership_commit' &&
    value.sourceSequence === 1 &&
    value.payload.owner === 'afterpay-mobile' &&
    (!('journeyId' in value.payload) || isUuid(value.payload.journeyId))
  ) {
    return value as unknown as OwnershipCommit
  }
  if (
    value.type === 'ownership_release' &&
    value.sourceSequence === 1 &&
    value.payload.owner === 'rocketship' &&
    value.payload.reason === 'mobile_runtime_unavailable'
  ) {
    return value as unknown as OwnershipRelease
  }
  return undefined
}
